import { GridState } from 'ag-grid-community';
import { InvestmentFilterOption } from '../pages/FundProfiles/Reserves/FundReservesFiltersAndActions';
import { StandardFormatterId } from '../util/formatter-service';
import { KpiPeriod } from './company-financials.data-model';
import { ICompanyDataModel } from './company.data-model';
import { IMetricsDataModel } from './metrics.data-model';

export enum PreferenceScope {
  USER = 'user',
  ORGANIZATION = 'organization',
}

export const PreferenceKey = {
  appCurrency: 'app.currency',
  auditorDateRanges: 'auditor.date-ranges',
  clientSettings: 'client-settings',
  captableSummaryGrid: 'captable-summary-grid',
  columnGridDisplay: 'column-grid-display',
  companyInvestments: 'company-profiles.summary.company-investments',
  companyPerformanceSelectedKPICharts: 'company-profiles.performance.selectedKpiCharts',
  companyPerformanceSelectedKPIs: 'company-profiles.performance.selectedKpis',
  companySummaryKPICharts: 'company-profiles.summary.kpiCharts',
  companySummaryFinancialSettings: 'company-profiles.summary.financial-settings',
  companySummaryLeadership: 'company-profiles.summary.leadership',
  companySummaryDetails: 'company-profiles.summary.company-details',
  companySummaryInvestments: 'company-profiles.summary.investments',
  companyListsOverview: 'company-profiles.lists.overview',
  dataCollectionEmail: 'data-collection.email',
  dealDrawerCompanyPanel: 'deal-flow.deal-drawer.company-panel',
  dealDrawerDealCustomDataPanel: 'deal-flow.deal-drawer.deal-custom-data-panel',
  dealDrawerMainDealPanel: 'deal-flow.deal-drawer.main-deal-panel',
  documentHubViewState: 'document-hub.view-state',
  entityManagementLineStyles: 'entity-management.structure-chart.line-styles',
  entityManagementSavedViews: 'entity-management.saved-views',
  entityRegistryGridState: 'entity-management.registry.grid-state',
  fundReservesViewState: 'fund-reserves.view-state',
  portfolioKpiCards: 'portfolio.kpi-cards',
  portfolioLandingView: 'portfolio.initialView',
  portfolioOverviewQuickSearchColumns: 'portfolio-overview.quick-search-company-columns',
  tearSheetCapitalizationSummary: 'tear-sheet.capitalization-summary.section',
  tearSheetCompanySummary: 'tear-sheet.company-summary.section',
  tearSheetCompanyDescription: 'tear-sheet.company-description.section',
  tearSheetFinancingHistory: 'tear-sheet.financing-description.section',
  tearSheetGeneral: 'tear-sheet.general',
  tearSheetInvestmentSummary: 'tear-sheet.investment-summary.section',
  tearSheetInvestmentThesis: 'tear-sheet.investment-thesis.section',
  tearSheetPerformanceCommentary: 'tear-sheet.performance-commentary.section',
  tearSheetPerformanceSummary: 'tear-sheet.performance-summary.section',
  tearSheetV1Layout: 'tear-sheet-v1.layout',
  tearSheetV2Layout: 'tear-sheet-v2.layout',
  defaultFinancialsDateView: 'company-profiles.financials.default-date-view',
  financialsKpiDisplay: 'company-profiles.financials.kpi-display',
  financialsCurrency: 'company-profiles.financials.currency',
  kpiSourceHierarchy: 'kpi.source-hierarchy',
  sensitiveMetrics: 'sensitive-metrics-fields',
} as const;

/**
 * If entityId is null, returns the default org preference key otherwise the specific one for the given entity id.
 */
export function getOrgPreferenceKey(baseKey: PreferenceKey, entityId: number | null): string {
  return entityId !== null ? `${baseKey}.$${entityId}` : `${baseKey}.$default`;
}

export type EmailTemplateMessage = {
  subject?: string;
  header?: string;
  body?: string;
  contact?: string;
  signature?: string;
  footer?: string;
};

export type AuditorDateRangesPreferences = {
  startDate: string; // Format: "2024-01-01" (date string)
  endDate: string; // Format: "2025-12-31" (date string)
  defaultFrequency: 'quarter' | 'month' | 'year'; // Display frequency for auditor financial data
  restrictToRange: boolean; // Enforce date restrictions
};

export type DataCollectionEmailPreferences = {
  ccEmails?: string[];
  senderName?: string | null;
  senderEmail?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  showHeader?: boolean;
  showPendingRequestsCount?: boolean;
  messages?: {
    request?: EmailTemplateMessage;
    reminder?: EmailTemplateMessage;
  };
};

export type SensitiveMetricsPreferences = keyof IMetricsDataModel | `company.${keyof ICompanyDataModel}`;

export type PreferenceKey = (typeof PreferenceKey)[keyof typeof PreferenceKey] | string;

export type PanelPreferences<T = string> = {
  availableFields?: T[];
  selectedFields: T[];
  visible?: boolean;
  fieldKeyToFormatterId?: Record<T extends string ? T : string, StandardFormatterId | string>;
};

export type TearSheetPerformanceSummaryPreferences = PanelPreferences & {
  period: KpiPeriod;
  viewType: 'tile' | 'table';
  relativePeriodDistances?: (0 | 1 | 2)[];
};
export type TearSheetGeneralPreferences = {
  showClientLogo?: boolean;
};
export type ClientSettingsPreferences = {
  isPrivateEquity?: boolean;
  logoUrl?: string;
  managedServices?: boolean;
  name?: string;
  displayName?: string;
  profileLogoUrl?: string;
};

export type FinancialsDateViewPreferences = {
  frequency: KpiPeriod;
  pastRange: number;
  futureRange: number | null;
};

export type FundReservesViewPreferences = {
  gridState?: GridState;
  investmentTypeFilter?: InvestmentFilterOption;
};

/**
 * Only the row grouping is stored, not the whole GridState: restoring a
 * forgotten sort or filter on the documents list reads as missing data.
 */
export type DocumentHubViewPreferences = {
  /** Legacy single-grid grouping (pre-FD-4647); read as the `all` view's fallback. */
  rowGroupColIds?: string[];
  /** Grouping per doc-type pill view (FD-4647), keyed by DocTypePill. */
  rowGroupColIdsByView?: Record<string, string[]>;
};

/**
 * The legal entity registry's column layout: which columns, in what order, at what width.
 * Sort and filters are deliberately not here, so a forgotten filter cannot read as missing
 * entities on the next visit.
 */
export type EntityRegistryGridPreferences = {
  gridState?: GridState;
};

export function createFundReservesViewPreferences(
  overrides: Partial<FundReservesViewPreferences> = {}
): FundReservesViewPreferences {
  return {
    ...overrides,
  };
}

export function createClientSettingsPreferences(
  overrides: Partial<ClientSettingsPreferences> = {}
): ClientSettingsPreferences {
  return {
    isPrivateEquity: false,
    logoUrl: '',
    name: '',
    managedServices: false,
    ...overrides,
  };
}

export interface IPreferencesV3DataModel<T> {
  scope: PreferenceScope;
  preferenceKey: PreferenceKey | string;
  entityId?: number | null;
  type: string | null;
  value: T;
}

export function createPreferenceV3DataModel<T>(
  overrides: Partial<IPreferencesV3DataModel<T>> = {}
): IPreferencesV3DataModel<T> {
  return {
    scope: PreferenceScope.USER,
    preferenceKey: '',
    entityId: null,
    type: null,
    value: {} as T,
    ...overrides,
  };
}

export function createPanelPreferences(overrides: Partial<PanelPreferences>): PanelPreferences {
  return { availableFields: [], selectedFields: [], visible: true, ...overrides };
}

export interface KpiDisplayPreferences {
  excludedFields: string[];
  fieldKeyToFormatterId: Record<string, string>;
}

export function createKpiDisplayPreferences(
  overrides?: Partial<KpiDisplayPreferences>
): KpiDisplayPreferences {
  return { excludedFields: [], fieldKeyToFormatterId: {}, ...overrides };
}

export const CaptableSummaryColumnType = {
  amount: 'amount',
  noOfShares: 'noOfShares',
  ownershipPercentage: 'ownershipPercentage',
} as const;
export type CaptableSummaryColumnType =
  (typeof CaptableSummaryColumnType)[keyof typeof CaptableSummaryColumnType];
export type CaptableSummaryGridPreferences = {
  investmentColumnOrder?: CaptableSummaryColumnType[];
  pinTotalColumns?: boolean;
  myInvestmentsOnly?: boolean;
};

export function createCaptableSummaryGridPreferences(
  overrides: Partial<CaptableSummaryGridPreferences> = {}
): CaptableSummaryGridPreferences {
  return {
    investmentColumnOrder: [],
    pinTotalColumns: false,
    myInvestmentsOnly: false,
    ...overrides,
  };
}
